import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { useUIStore } from '@/store/useUIStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DropdownActionType } from '@/types/shared';
import CardSales from '@/components/Admin/CardSales';
import CardSalesTop from '@/components/Admin/CardSalesTop';
import SectionTitle from '@/components/Admin/SectionTitle';
import Table from '@/components/Admin/Table';
import DateRangePicker from '@/components/Admin/UI/DateRangePicker';
import {
   getMyVoucherDashboardApi,
   listMyVoucherTransactionsApi,
} from '@/api/vendo';
import useVoucherTxnFilterStore, {
   VoucherTxnStatusFilter,
} from '@/store/useVoucherTxnFilterStore';
import { VoucherInventoryModal } from '@/components/Admin/Prompt/DeviceSettings';
import WifiVouchersCounter from './WifiVouchersCounter';

/**
 * /wifi-vouchers — operator-facing voucher dashboard for Piso WiFi.
 * Mirrors the voucher-store dashboard's e-wallet feel: simple,
 * straightforward, sections clearly separated. The CardSalesTop and
 * CardSales components are reused as-is. The only structural addition
 * vs. /voucher is a "Select date range" picker right above the
 * transaction table so operators can scope the table to a window.
 *
 * Data sources are vendo-only — every fetch hits /api/vendo/voucher/*
 * endpoints which filter by VendoTransaction + Voucher.vendoId.
 * No voucher-store data leaks in. Withdraw button routes the payout to
 * the vendo Payout collection (source='vendo') via PayoutForm's source
 * prop.
 */

type TableRow = {
   _id: string;
   updatedAt: string;
   createdAt: string;
   status: string;
   voucherCode: string;
   store: string;
   amount: number;
   platformFee: number;
   xenditFee: number;
   netAmount: number;
   vat: number;
   customer: string;
   smsStatus?: string;
   smsError?: string;
};

const WifiVouchers = () => {
   const { handleShow, refresh } = useUIStore();
   const navigate = useNavigate();
   // Two tabs (Ray 2026-09-15): online-paid vouchers vs printed ones
   // sold by hand.
   // The tab is in the address (?tab=counter) so the sidebar sub-items can
   // open either one directly (Ray 2026-10-14).
   const [searchParams, setSearchParams] = useSearchParams();
   // Over the counter is the default tab (Ray 2026-10-18).
   const tab: 'epay' | 'counter' = searchParams.get('tab') === 'epay' ? 'epay' : 'counter';
   const setTab = (next: 'epay' | 'counter') => setSearchParams({ tab: next }, { replace: true });

   // Date range — empty strings mean "all time". Applied to the
   // transaction list query so the table reflects whatever window the
   // operator picked. Cards above stay all-time / built-in periods
   // (today / week / month).
   const [dateFrom, setDateFrom] = useState<string>('');
   const [dateTo, setDateTo] = useState<string>('');

   // Persisted status filter — defaults to 'success' so operators see
   // completed payments first. Stored in zustand+localStorage so the
   // choice survives reloads and follows the operator across devices.
   const statusFilter = useVoucherTxnFilterStore((s) => s.statusFilter);
   const setStatusFilter = useVoucherTxnFilterStore((s) => s.setStatusFilter);

   const { data: dashboard } = useQuery({
      queryKey: ['wifi-voucher-dashboard', refresh],
      queryFn: getMyVoucherDashboardApi,
   });

   const { data: txnResponse } = useQuery({
      queryKey: ['wifi-voucher-txns', refresh, dateFrom, dateTo, statusFilter],
      queryFn: () =>
         listMyVoucherTransactionsApi({
            page: 1,
            limit: 500,
            from: dateFrom || undefined,
            to: dateTo || undefined,
            status:
               statusFilter !== 'all'
                  ? (statusFilter as 'success' | 'pending')
                  : undefined,
         }),
   });

   const transactionData: TableRow[] = useMemo(
      () =>
         (txnResponse?.rows ?? []).map((t) => ({
            _id: t._id,
            updatedAt: t.createdAt,
            createdAt: t.createdAt,
            status: t.status,
            voucherCode: t.voucherCode || '',
            store: t.vendoDevice?.name ?? '—',
            amount: t.amount,
            platformFee: t.platformFee,
            xenditFee: t.xenditFee,
            netAmount: t.netAmount,
            vat: t.vat,
            customer: t.customer,
            smsStatus: '',
            smsError: '',
         })),
      [txnResponse]
   );

   const PER_PAGE = 10;
   const [page, setPage] = useState(1);
   const [reprintOpen, setReprintOpen] = useState(false);
   const totalPages = Math.max(1, Math.ceil(transactionData.length / PER_PAGE));

   useEffect(() => {
      if (page > totalPages) setPage(1);
   }, [totalPages, page]);

   // Reset to page 1 whenever the filter changes — otherwise the
   // operator can land on a page index that no longer has rows after
   // the filtered set shrinks.
   useEffect(() => {
      setPage(1);
   }, [statusFilter]);

   const paginatedTransactions = useMemo(
      () => transactionData.slice((page - 1) * PER_PAGE, page * PER_PAGE),
      [transactionData, page]
   );

   const transactionHeadData = [
      { type: 'title', value: 'Date' },
      { type: 'title', value: 'Status' },
      { type: 'title', value: 'Voucher Code' },
      { type: 'title', value: 'Device' },
      { type: 'title', value: 'Amount' },
      { type: 'title', value: 'Contact' },
      { type: 'title', value: 'SMS' },
      { type: 'title', value: 'Action' },
   ];

   const dropdownActions: DropdownActionType<TableRow>[] = [
      {
         label: 'View Details',
         onClick: (data: TableRow) => {
            handleShow({
               template: 'transaction-detail',
               title: 'Transaction Details',
               windowType: 'desktop',
               data: data,
            });
         },
         icon: 'file-copy-line',
      },
   ];

   const handleVendoWithdraw = () => {
      handleShow({
         template: 'payout',
         title: 'Request a Payout',
         data: { source: 'vendo' },
      });
   };
   const handleVendoViewAllPayouts = () => navigate('/wifi-voucher-payout');

   return (
      <Wrapper>
         <TabRow role="tablist">
            <FilterChip
               role="tab"
               aria-selected={tab === 'counter'}
               $active={tab === 'counter'}
               onClick={() => setTab('counter')}
            >
               Over the counter
            </FilterChip>
            <FilterChip
               role="tab"
               aria-selected={tab === 'epay'}
               $active={tab === 'epay'}
               onClick={() => setTab('epay')}
            >
               E-payments / GCash
            </FilterChip>
         </TabRow>

         {tab === 'counter' && <WifiVouchersCounter />}

         {tab === 'epay' && (
         <>
         <CardsContent>
            <CardSalesTop
               totalSalesTodayAndYesterday={
                  dashboard?.todayYesterday ?? {
                     today: 0,
                     yesterday: 0,
                     percentage: 0,
                  }
               }
               totalSalesThisWeekAndLastWeek={
                  dashboard?.weekLastWeek ?? {
                     thisWeek: 0,
                     lastWeek: 0,
                     percentage: 0,
                  }
               }
               totalSalesThisMonthAndLastMonth={
                  dashboard?.monthLastMonth ?? {
                     thisMonth: 0,
                     lastMonth: 0,
                     percentage: 0,
                  }
               }
            />
            <CardSales
               totalPayoutReceived={dashboard?.totalPayoutReceived ?? 0}
               totalPayoutAvailable={dashboard?.totalPayoutAvailable ?? 0}
               soldVouchers={dashboard?.soldVouchers ?? 0}
               availableVouchers={dashboard?.availableVouchers ?? 0}
               totalSales={dashboard?.totalSales ?? 0}
               onWithdrawClick={handleVendoWithdraw}
               onViewAllClick={handleVendoViewAllPayouts}
               availableVouchersLabel="Unredeemed Vouchers"
               onReprintClick={() => setReprintOpen(true)}
            />
         </CardsContent>

         {reprintOpen && (
            <VoucherInventoryModal onClose={() => setReprintOpen(false)} />
         )}

         <ContentItem>
            <SectionHead>
               <SectionTitle title="Transaction History" />
               <DatePickerWrap>
                  <DateRangePicker
                     dateFrom={dateFrom}
                     dateTo={dateTo}
                     onChange={(from, to) => {
                        setDateFrom(from);
                        setDateTo(to);
                        setPage(1);
                     }}
                  />
               </DatePickerWrap>
            </SectionHead>
            <FilterRow>
               {(['success', 'pending', 'all'] as const).map((s) => (
                  <FilterChip
                     key={s}
                     $active={statusFilter === s}
                     onClick={() =>
                        setStatusFilter(s as VoucherTxnStatusFilter)
                     }
                  >
                     {s.charAt(0).toUpperCase() + s.slice(1)}
                  </FilterChip>
               ))}
            </FilterRow>
            <Table<TableRow>
               headData={transactionHeadData}
               data={paginatedTransactions}
               grid={`110px 1fr 2fr 1fr 1fr 1fr 90px auto`}
               type="transaction"
               dropdownData={dropdownActions}
               onRowClick={(data) => {
                  handleShow({
                     template: 'transaction-detail',
                     title: 'Transaction Details',
                     windowType: 'desktop',
                     data: data,
                  });
               }}
            />

            {totalPages > 1 && (
               <Pagination>
                  <PageBtn
                     disabled={page === 1}
                     onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                     Prev
                  </PageBtn>
                  <PageNumbers>
                     {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((p) => {
                           if (totalPages <= 7) return true;
                           if (p === 1 || p === totalPages) return true;
                           if (Math.abs(p - page) <= 1) return true;
                           return false;
                        })
                        .reduce<(number | string)[]>((acc, p, idx, arr) => {
                           if (idx > 0 && p - (arr[idx - 1] as number) > 1) {
                              acc.push('...');
                           }
                           acc.push(p);
                           return acc;
                        }, [])
                        .map((p, idx) =>
                           typeof p === 'string' ? (
                              <Ellipsis key={`e-${idx}`}>...</Ellipsis>
                           ) : (
                              <PageNum
                                 key={p}
                                 $active={p === page}
                                 onClick={() => setPage(p)}
                              >
                                 {p}
                              </PageNum>
                           )
                        )}
                  </PageNumbers>
                  <PageBtn
                     disabled={page === totalPages}
                     onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                     Next
                  </PageBtn>
               </Pagination>
            )}
         </ContentItem>
         </>
         )}
      </Wrapper>
   );
};

export default WifiVouchers;

/* ─── styled (mirrors Home.tsx but with date picker + vendo Withdraw bar) */

const Wrapper = styled.div``;

const TabRow = styled.div`
   display: flex;
   gap: 8px;
   margin-top: 1rem;
   padding: 0 1rem;
   @media (max-width: 768px) {
      margin-top: 12px;
      padding: 0;
   }
`;

const CardsContent = styled.div`
   margin-top: 1rem;
   @media (max-width: 768px) {
      margin-top: 12px;
   }
`;

const ContentItem = styled.div`
   margin-top: 1.5rem;
   @media (max-width: 768px) {
      margin-top: 16px;
   }
`;

const SectionHead = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   padding-right: 1rem;
   margin-bottom: 4px;

   @media (max-width: 768px) {
      padding: 0 12px;
      flex-direction: column;
      align-items: stretch;
   }
`;

const DatePickerWrap = styled.div`
   /* DateRangePicker exposes its own button — wrap so we can position
      it inline with the section title on desktop and stack on mobile. */
`;

const FilterRow = styled.div`
   display: flex;
   gap: 8px;
   padding: 8px 1rem 12px;

   @media (max-width: 768px) {
      padding: 8px 12px 12px;
   }
`;

const FilterChip = styled.button<{ $active: boolean }>`
   padding: 6px 14px;
   border-radius: 999px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   border: 1px solid
      ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY13)};
   background: ${({ $active }) =>
      $active ? colors.PRIMARY : 'transparent'};
   color: ${({ $active }) => ($active ? '#fff' : colors.GRAY3)};
   transition: all 0.15s;

   &:hover {
      border-color: ${colors.PRIMARY};
   }
`;

const Pagination = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 8px;
   padding: 16px 1rem;
`;

const PageBtn = styled.button<{ disabled?: boolean }>`
   display: flex;
   align-items: center;
   gap: 4px;
   background: none;
   border: 1px solid
      ${({ disabled }) => (disabled ? colors.GRAY12 : colors.GRAY10)};
   border-radius: 4px;
   padding: 6px 12px;
   font-size: 13px;
   color: ${({ disabled }) => (disabled ? colors.GRAY10 : colors.GRAY1)};
   cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

   &:hover:not(:disabled) {
      background: ${colors.GRAY14};
   }
`;

const PageNumbers = styled.div`
   display: flex;
   align-items: center;
   gap: 4px;
`;

const PageNum = styled.button<{ $active: boolean }>`
   background: ${({ $active }) => ($active ? colors.PRIMARY : 'none')};
   color: ${({ $active }) => ($active ? 'white' : colors.GRAY1)};
   border: 1px solid
      ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 4px;
   width: 32px;
   height: 32px;
   font-size: 13px;
   cursor: pointer;

   &:hover {
      background: ${({ $active }) =>
         $active ? colors.PRIMARY : colors.GRAY14};
   }
`;

const Ellipsis = styled.span`
   color: ${colors.GRAY5};
   font-size: 13px;
   padding: 0 4px;
`;
